import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { Icon } from "@iconify/react";
import { HStack, VStack } from "../../../components/utils";
import {
  CorporateActionParams,
  OptionsRepricingDetails,
} from "../../../types/CompanyProfile";
import OptionsRepricingStepper from "./OptionsRepricingStepper";
import OptionsRepricingNavigation from "./OptionsRepricingNavigation";
import BCHeader from "../../../shared/BCHeader";

const defaultValues: OptionsRepricingDetails = {
  name: "",
  date: new Date().toISOString().split("T")[0],
  newStrikePrice: 0,
  repricingType: "VESTING",
  showOptionsRepricingSummary: false,
  state: "DRAFT",
  note: "",
  filters: {},
  grantPriceOverrides: {},
  vestingLevelOverrides: {},
};

function paramsToFormValues(
  record: CorporateActionParams
): OptionsRepricingDetails {
  return {
    id: record.id,
    name: record.name,
    date: String(record.date).split("T")[0],
    newStrikePrice: record.metaData?.repricingDetails?.newStrikePrice ?? 0,
    repricingType: record.metaData?.repricingDetails?.repricingType ?? "GRANT",
    showOptionsRepricingSummary:
      record.metaData?.repricingDetails?.showOptionsRepricingSummary ?? false,
    state: (record.state as "DRAFT" | "APPROVED") ?? "DRAFT",
    note: record.note,
    reasonForRepricing: record.metaData?.repricingDetails?.reasonForRepricing,
    boardResolutionReference:
      record.metaData?.repricingDetails?.boardResolutionReference,
    supportingDocuments: record.metaData?.repricingDetails?.supportingDocuments,
    filters: {
      planIds: record.metaData?.filters?.planIds,
      employmentStatus: record.metaData?.filters?.employmentStatus,
      employmentType: record.metaData?.filters?.employeeType,
      grantState: record.metaData?.filters?.grantState,
      grantDateFrom: record.metaData?.filters?.dateOfGrantFrom,
      grantDateTo: record.metaData?.filters?.dateOfGrantTo,
      grantIds: record.metaData?.filters?.grantIds,
    },
    grantPriceOverrides:
      record.metaData?.repricingDetails?.grantStrikePriceOverrides,
    vestingLevelOverrides:
      record.metaData?.repricingDetails?.vestingStrikePriceOverrides,
  };
}

const validationSchemas = [
  Yup.object({
    name: Yup.string().required("Name is required"),
    date: Yup.date().required("Date is required"),
    newStrikePrice: Yup.number()
      .positive("New strike price must be positive")
      .required("New strike price is required"),
    repricingType: Yup.string()
      .oneOf(["GRANT", "VESTING"])
      .required("Repricing type is required"),
  }),
  Yup.object({}),
  Yup.object({}),
  Yup.object({}),
];

export default function OptionsRepricingPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const existingRecord: CorporateActionParams | undefined = (state as any)
    ?.record;
  const readonly: boolean = !!(state as any)?.readonly;
  const [currentStep, setCurrentStep] = useState(1);

  function onStepChange(step?: number) {
    setCurrentStep(step ?? currentStep + 1);
  }

  function onBackClick() {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  }

  function onClose() {
    navigate("/settings/companyProfile");
  }

  const formik = useFormik({
    initialValues: existingRecord
      ? paramsToFormValues(existingRecord)
      : defaultValues,
    validationSchema: validationSchemas[currentStep - 1],
    onSubmit: () => {},
    enableReinitialize: true,
  });

  return (
    <FormikProvider value={formik}>
      <VStack className="w-full min-h-screen">
        <HStack className="items-center justify-between pr-6">
          <BCHeader
            className="items-start px-6 py-4"
            bcTitle="Company Settings"
            bcSubTitle={
              readonly
                ? "View Options Repricing"
                : existingRecord
                ? "Edit Options Repricing"
                : "New Options Repricing"
            }
            onClick={onClose}
          />
        </HStack>
        <HStack className="flex-1 gap-5">
          {!readonly && (
            <VStack className="flex w-[25%] bg-white rounded-md min-w-72">
              <OptionsRepricingStepper
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
              />
            </VStack>
          )}
          <div
            className={`${readonly ? "w-full" : "w-3/4"} bg-white rounded-md`}
          >
            <OptionsRepricingNavigation
              currentStep={currentStep}
              validationSchema={validationSchemas[currentStep - 1]}
              onStepChange={onStepChange}
              onBackClick={onBackClick}
              onClose={onClose}
              readonly={readonly}
              s3url={existingRecord?.s3url}
            />
          </div>
        </HStack>
      </VStack>
    </FormikProvider>
  );
}
